/**
 * Product card metric hooks (superadmin).
 *
 * Reads the metrics linked to a product card, their time series, and the
 * batched card-sparkline series for the whole board; plus mutations to collect
 * a metric on demand and link/unlink a metric to a card. All endpoints are
 * superadmin-gated (`/superadmin/product-metrics/*`).
 *
 * @module features/product-board/hooks/useProductCardMetrics
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';

export interface ProductCardMetric {
  id: string;
  name: string;
  title: string | null;
  description: string | null;
  source: 'postgres' | 'cloudwatch';
  unit: string | null;
  cadenceSeconds: number;
  enabled: boolean;
  lastValue: number | null;
  lastCollectedAt: string | null;
  lastError: string | null;
}

export interface MetricDatapoint {
  value: number | null;
  error: string | null;
  capturedAt: string;
}

export interface CardSparkline {
  cardId: string;
  metricName: string;
  unit: string | null;
  values: number[];
}

export interface PageSkillsAnalysisGroup {
  orgId: string;
  orgName: string;
  propertyId: string;
  pageIdentity: string;
  pathPattern: string | null;
  skillVersion: number | null;
  visits: number;
  presented: number;
  reads: number;
  upserts: number;
  runCount: number;
  stepOutcomeCount: number;
  stepSuccessCount: number;
  terminalOutcomeCount: number;
  terminalSuccessCount: number;
}

export interface PageSkillsAnalysis {
  applicable: boolean;
  windowDays: 7 | 30 | 90;
  groups: PageSkillsAnalysisGroup[];
}

export interface PageSkillsContinuousMetric {
  n: number;
  mean: number | null;
  sd: number | null;
}

export interface PageSkillsRateMetric {
  n: number;
  positive: number;
  rate: number | null;
}

export interface PageSkillsExperimentArmAnalysis {
  exposureMatchedStepCount: number;
  turnLogCoverage: PageSkillsRateMetric;
  turnsPerRun: PageSkillsContinuousMetric;
  tokensPerRun: PageSkillsContinuousMetric;
  stepSuccess: PageSkillsRateMetric;
  guardrails: {
    runSuccess: PageSkillsRateMetric;
    cacheHitRate: PageSkillsRateMetric;
  };
}

export interface PageSkillsExperimentAnalysis {
  applicable: boolean;
  windowDays: 7 | 30 | 90;
  arms: {
    treatment: PageSkillsExperimentArmAnalysis;
    control: PageSkillsExperimentArmAnalysis;
  };
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (!res.ok) {
    if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
    throw new Error(`Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

/** Metrics linked to a single product card (drawer). */
export function useProductCardMetrics(cardId: string | null | undefined) {
  return useQuery({
    queryKey: ['product-card-metrics', cardId],
    enabled: Boolean(cardId),
    queryFn: () =>
      getJson<{ ok: true; metrics: ProductCardMetric[] }>(
        `/superadmin/product-metrics/by-product-card/${cardId}`
      ).then((r) => r.metrics),
  });
}

/** Full list of the org's metrics (used by the "link metric" picker). */
export function useAllMetrics(enabled: boolean) {
  return useQuery({
    queryKey: ['superadmin-metrics-all'],
    enabled,
    queryFn: () =>
      getJson<{ ok: true; metrics: ProductCardMetric[] }>(`/superadmin/product-metrics`).then(
        (r) => r.metrics
      ),
  });
}

/** Time series for one metric (drawer chart). */
export function useMetricDatapoints(name: string | null | undefined, since?: string) {
  return useQuery({
    queryKey: ['metric-datapoints', name, since ?? null],
    enabled: Boolean(name),
    queryFn: () => {
      const qs = since ? `?since=${encodeURIComponent(since)}` : '';
      return getJson<{ ok: true; datapoints: MetricDatapoint[] }>(
        `/superadmin/product-metrics/${encodeURIComponent(name!)}/datapoints${qs}`
      ).then((r) => r.datapoints);
    },
  });
}

/** Batched sparkline series for every card with a linked metric (board). */
export function useCardSparklines(enabled: boolean) {
  return useQuery({
    queryKey: ['metric-card-sparklines'],
    enabled,
    queryFn: () =>
      getJson<{ ok: true; sparklines: CardSparkline[] }>(
        `/superadmin/product-metrics/card-sparklines`
      ).then((r) => r.sparklines),
  });
}

/** Descriptive, superadmin-only breakdown for the Page Skills product card. */
export function usePageSkillsAnalysis(
  cardId: string | null | undefined,
  windowDays: 7 | 30 | 90,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['page-skills-analysis', cardId, windowDays],
    enabled: enabled && Boolean(cardId),
    queryFn: () =>
      getJson<{ ok: true; analysis: PageSkillsAnalysis }>(
        `/superadmin/product-board/items/${cardId}/page-skills-analysis?windowDays=${windowDays}`
      ).then((r) => r.analysis),
  });
}

/** Exposure-matched two-arm Page Skills experiment read model. */
export function usePageSkillsExperiment(
  cardId: string | null | undefined,
  windowDays: 7 | 30 | 90,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['page-skills-experiment', cardId, windowDays],
    enabled: enabled && Boolean(cardId),
    queryFn: () =>
      getJson<{ ok: true; analysis: PageSkillsExperimentAnalysis }>(
        `/superadmin/product-board/items/${cardId}/page-skills-experiment?windowDays=${windowDays}`
      ).then((r) => r.analysis),
  });
}

/** Collect a metric's value now; refreshes the card metrics + datapoints. */
export function useCollectMetric(cardId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      postJson<{
        ok: true;
        skipped: boolean;
        reason?: string;
        datapoint: MetricDatapoint | null;
      }>(`/superadmin/product-metrics/${encodeURIComponent(name)}/collect`),
    onSuccess: (_data, name) => {
      qc.invalidateQueries({ queryKey: ['product-card-metrics', cardId] });
      qc.invalidateQueries({ queryKey: ['metric-datapoints', name] });
      qc.invalidateQueries({ queryKey: ['metric-card-sparklines'] });
    },
  });
}

/** Link or unlink a metric to a product card. */
export function useLinkMetricToCard(cardId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, action }: { name: string; action: 'link' | 'unlink' }) =>
      postJson<{ ok: true }>(`/superadmin/product-metrics/${encodeURIComponent(name)}/cards`, {
        cardId,
        action,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-card-metrics', cardId] });
      qc.invalidateQueries({ queryKey: ['metric-card-sparklines'] });
    },
  });
}
